import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert } from 'react-bootstrap';
import { DataTable, FilterBar, FilterBarChip, KpiTile, StatusPill, SortState } from '../patterns';
import { getStockMap, StockEntry } from '../../services/consumableInventoryService';
import type { ConsumableType } from '../../services/consumableService';
import { formatMeasure } from '../../utils/formatMeasure';

const HEALTHY_THRESHOLD = 50;
const LOW_THRESHOLD = 10;

interface KpiSummary {
  total: number;
  healthyCount: number;
  healthyUnits: number;
  lowCount: number;
  criticalCount: number;
}

function summarise(stockLevels: StockEntry[]): KpiSummary {
  return stockLevels.reduce<KpiSummary>(
    (acc, item) => {
      acc.total += 1;
      // A unit-conflict row has no numeric stock to bucket — it contributes
      // to the total product count only, never to the healthy/low/critical tallies.
      if (item.currentStock == null) return acc;
      if (item.currentStock >= HEALTHY_THRESHOLD) {
        acc.healthyCount += 1;
        acc.healthyUnits += item.currentStock;
      } else if (item.currentStock >= LOW_THRESHOLD) {
        acc.lowCount += 1;
      } else {
        acc.criticalCount += 1;
      }
      return acc;
    },
    { total: 0, healthyCount: 0, healthyUnits: 0, lowCount: 0, criticalCount: 0 },
  );
}

type StatusVariant = 'ok' | 'warn' | 'danger';

function statusVariant(currentStock: number | null): StatusVariant {
  if (currentStock == null) return 'danger';
  if (currentStock >= HEALTHY_THRESHOLD) return 'ok';
  if (currentStock >= LOW_THRESHOLD)     return 'warn';
  return 'danger';
}

const TYPE_CHIPS: { id: ConsumableType; labelKey: string }[] = [
  { id: 'chemical',   labelKey: 'consumables.catalog.filters.type.chemical' },
  { id: 'ppe',        labelKey: 'consumables.catalog.filters.type.ppe' },
  { id: 'fertilizer', labelKey: 'consumables.catalog.filters.type.fertilizer' },
  { id: 'diesel',     labelKey: 'consumables.catalog.filters.type.diesel' },
];

export interface StockLevelsTableProps {
  /** When set, hides the chip filter and locks the consumableType param. */
  scope?: ConsumableType;
}

const StockLevelsTable: React.FC<StockLevelsTableProps> = ({ scope }) => {
  const { t } = useTranslation();
  const [rows, setRows] = useState<StockEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [activeChip, setActiveChip] = useState<ConsumableType | null>(null);
  // Default to alphabetical (A→Z) by product name; headers toggle from here.
  const [sort, setSort] = useState<SortState>({ key: 'product', direction: 'asc' });

  const effectiveType: ConsumableType | undefined = scope ?? activeChip ?? undefined;

  const load = () => {
    setLoading(true);
    setError('');
    const params: { consumableType?: ConsumableType } = {};
    if (effectiveType) params.consumableType = effectiveType;
    getStockMap(params)
      .then((data) => { setRows(data); setLoading(false); })
      .catch((err: { message?: string }) => { setError(err.message ?? t('common.error', { defaultValue: 'Failed to load' })); setLoading(false); });
  };

  useEffect(() => {
    load();
    // load() reads t() only on the error branch; intentionally excluded from
    // deps so a changing t identity doesn't retrigger the fetch (exhausts test mocks).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveType]);

  const summary = useMemo(() => summarise(rows), [rows]);

  const visibleRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => r.productName.toLowerCase().includes(q));
  }, [rows, search]);

  // Client-side sort over the (already search-filtered) rows. 'product' sorts by
  // name (numeric-aware so "Item 9" precedes "Item 10"); 'currentStock' sorts
  // numerically. DataTable is presentational — the parent owns sort + ordering.
  // A unit-conflict row (currentStock: null) has nothing to compare numerically,
  // so it always sorts last regardless of ascending/descending direction.
  const sortedRows = useMemo(() => {
    const dir = sort.direction === 'asc' ? 1 : -1;
    return [...visibleRows].sort((a, b) => {
      if (sort.key === 'currentStock') {
        if (a.currentStock == null && b.currentStock == null) return 0;
        if (a.currentStock == null) return 1;
        if (b.currentStock == null) return -1;
        return (a.currentStock - b.currentStock) * dir;
      }
      return a.productName.localeCompare(b.productName, undefined, { numeric: true, sensitivity: 'base' }) * dir;
    });
  }, [visibleRows, sort]);

  const chips: FilterBarChip[] | undefined = scope
    ? undefined
    : TYPE_CHIPS.map((c) => ({ id: c.id, label: t(c.labelKey), active: activeChip === c.id }));

  if (loading) return <div>{t('common.loading')}</div>;
  if (error) {
    return (
      <Alert variant="danger" role="alert">
        <div>{error}</div>
        <button type="button" className="pat-btn pat-btn--subtle" onClick={load}>
          {t('common.retry', { defaultValue: 'Retry' })}
        </button>
      </Alert>
    );
  }

  const formatProductName = (r: StockEntry): string => {
    const base = r.lNumber ? `${r.productName} (${r.lNumber})` : r.productName;
    return r.deletedAt
      ? `${base} ${t('consumables.shared.deletedSuffix', { defaultValue: '(deleted)' })}`
      : base;
  };

  const emptyKey = scope === 'chemical' ? 'spray.stockLevels.empty' : 'consumables.stockLevels.empty';

  const statusLabel = (variant: StatusVariant): string => {
    if (variant === 'ok')   return t('spray.inventory.kpi.healthy',  { defaultValue: 'Healthy' });
    if (variant === 'warn') return t('spray.inventory.kpi.low',      { defaultValue: 'Low' });
    return t('spray.inventory.kpi.critical', { defaultValue: 'Critical' });
  };

  const isConflict = (r: StockEntry): boolean => r.stockStatus === 'conflict' || r.currentStock == null;

  const conflictBadge = () => (
    <StatusPill variant="warn">{t('inventory.unitConflict.badge')}</StatusPill>
  );

  // On-hand rescaled to a readable unit (18000 ml -> 18 L). Conflict rows are
  // rendered as a badge upstream (isConflict), so this is only the ok path.
  const stockCellText = (r: StockEntry): string => formatMeasure(r.currentStock, r.stockUnit);

  return (
    <div data-testid="stock-levels-table">
      <div className="pat-kpi-row">
        <KpiTile label={t('spray.inventory.kpi.totalProducts', { defaultValue: 'Total products' })} value={summary.total} />
        <KpiTile
          label={t('spray.inventory.kpi.healthy', { defaultValue: 'Healthy' })}
          value={summary.healthyCount}
          delta={summary.healthyCount > 0
            ? t('spray.inventory.kpi.unitsSuffix', { count: summary.healthyUnits, defaultValue: '{{count}} units' })
            : undefined}
        />
        <KpiTile label={t('spray.inventory.kpi.low', { defaultValue: 'Low' })} value={summary.lowCount} />
        <KpiTile
          label={t('spray.inventory.kpi.critical', { defaultValue: 'Critical' })}
          value={summary.criticalCount}
          alert={summary.criticalCount > 0}
        />
      </div>

      <FilterBar
        searchValue={search}
        onSearchChange={setSearch}
        chips={chips}
        onChipClick={chips ? ((id) => setActiveChip((prev) => (prev === id ? null : (id as ConsumableType)))) : undefined}
      />

      <DataTable<StockEntry>
        rows={sortedRows}
        rowKey={(r) => r.productId}
        sort={sort}
        onSortChange={setSort}
        columns={[
          {
            key: 'product',
            label: t('consumables.stockCount.columns.product', { defaultValue: 'Product' }),
            sortable: true,
            render: (r) => formatProductName(r),
          },
          ...(scope === 'chemical'
            ? []
            : [{
                key: 'type',
                label: t('consumables.catalog.list.columns.consumableType', { defaultValue: 'Type' }),
                render: (r: StockEntry) => r.consumableType ?? '',
              }]),
          {
            key: 'currentStock',
            label: t('consumables.stockCount.columns.systemStock', { defaultValue: 'Current stock' }),
            align: 'right',
            sortable: true,
            render: (r) => (isConflict(r) ? conflictBadge() : stockCellText(r)),
          },
          {
            key: 'status',
            label: t('common.status', { defaultValue: 'Status' }),
            align: 'right',
            render: (r) => {
              if (isConflict(r)) return conflictBadge();
              const v = statusVariant(r.currentStock);
              return <StatusPill variant={v}>{statusLabel(v)}</StatusPill>;
            },
          },
        ]}
        emptyState={t(emptyKey)}
        mobileCardRow={(r) => (
          <div>
            <strong>{formatProductName(r)}</strong>
            <div style={{ fontSize: 11, color: 'var(--shell-text-muted)' }}>
              {isConflict(r) ? (
                conflictBadge()
              ) : (
                <>
                  {stockCellText(r)}
                  {' · '}
                  <StatusPill variant={statusVariant(r.currentStock)}>{statusLabel(statusVariant(r.currentStock))}</StatusPill>
                </>
              )}
            </div>
          </div>
        )}
      />
    </div>
  );
};

export default StockLevelsTable;
